import type { CustomLocationHook } from '@/types/useCustomLocation'
import { useCallback, useEffect, type SetStateAction } from 'react'
import { useLocation, useSearch } from 'wouter'

export function useStringParam(
	name: string,
	defaultValue: string,
	deleteIfDefault = true,
): [value: string, setValue: (next: SetStateAction<string>) => void] {
	const search = useSearch()
	const [, setLocation] = useLocation<CustomLocationHook>()

	const params = new URLSearchParams(search)
	const value = params.get(name) ?? defaultValue

	const setValue = useCallback(
		(set: string | ((prev: string) => string)) => {
			const nextValue = typeof set === 'function' ? set(value) : set
			if (deleteIfDefault && nextValue === defaultValue) {
				setLocation('', { removeParams: [name] })
			} else {
				setLocation('', { setParams: [[name, nextValue]] })
			}
		},
		[name, value, defaultValue, deleteIfDefault],
	)

	return [value, setValue]
}

export function useNumberParam(
	name: string,
	defaultValue: number,
	deleteIfDefault = true,
): [value: number, setValue: (next: SetStateAction<number>) => void] {
	const [rawValue, setRawValue] = useStringParam(
		name,
		'' + defaultValue,
		deleteIfDefault,
	)

	const value = isNaN(+rawValue) ? defaultValue : +rawValue
	const setValue = (set: number | ((prev: number) => number)) => {
		setRawValue(rawPrev => {
			const prev = isNaN(+rawPrev) ? defaultValue : +rawPrev
			const next = typeof set === 'function' ? set(prev) : set
			return '' + next
		})
	}

	return [value, setValue]
}

function castStringToBoolean(value: string, defaultValue: boolean): boolean {
	if (value === '0' || value === 'false') return false
	if (value === '1' || value === 'true') return true
	return defaultValue
}
function castBooleanToString(value: boolean): string {
	return value ? '1' : '0'
}

export function useBooleanParam(
	name: string,
	defaultValue: boolean,
	deleteIfDefault = true,
): [value: boolean, setValue: (next: SetStateAction<boolean>) => void] {
	const [rawValue, setRawValue] = useStringParam(
		name,
		castBooleanToString(defaultValue),
		deleteIfDefault,
	)

	const value = castStringToBoolean(rawValue, defaultValue)
	const setValue = (set: boolean | ((prev: boolean) => boolean)) => {
		setRawValue(rawPrev => {
			const prev = castStringToBoolean(rawPrev, defaultValue)
			const next = typeof set === 'function' ? set(prev) : set
			return castBooleanToString(next)
		})
	}

	return [value, setValue]
}

export function useProtectedStringParam(
	name: string,
	defaultValue: string,
	isValid: (value: string) => boolean,
	deleteIfDefault = true,
): [value: string, setValue: (next: SetStateAction<string>) => void] {
	const [rawValue, setRawValue] = useStringParam(
		name,
		defaultValue,
		deleteIfDefault,
	)

	const value = isValid(rawValue) ? rawValue : defaultValue

	const setValue = (set: string | ((prev: string) => string)) => {
		const nextValue = typeof set === 'function' ? set(value) : set
		if (isValid(nextValue)) {
			setRawValue(nextValue)
		} else {
			setRawValue(defaultValue)
		}
	}

	useEffect(() => {
		if (!isValid(rawValue) && isValid(defaultValue)) {
			setRawValue(defaultValue)
		}
	}, [rawValue, setRawValue, isValid])

	return [value, setValue]
}

export function useListParam(
	name: string,
	defaultValue: readonly string[],
	deleteIfDefault = true,
): [
	value: readonly string[],
	setValue: (next: SetStateAction<readonly string[]>) => void,
	toggleValue: (item: string) => void,
	defineValue: (item: string, active: boolean) => void,
] {
	const [rawValue, setRawValue] = useStringParam(
		name,
		defaultValue.join(','),
		deleteIfDefault,
	)

	const value = [
		...new Set(
			rawValue
				.split(',')
				.map(x => x.trim())
				.filter(Boolean),
		),
	]

	const setValue = useCallback(
		(
			set:
				| readonly string[]
				| ((prev: readonly string[]) => readonly string[]),
		): void =>
			setRawValue(rawPrev => {
				const prev = [
					...new Set(
						rawPrev
							.split(',')
							.map(x => x.trim())
							.filter(Boolean),
					),
				]
				const next = typeof set === 'function' ? set(prev) : set
				return [...new Set(next)].join(',')
			}),
		[setRawValue],
	)

	const toggleValue = useCallback(
		(item: string) =>
			setValue(prev => {
				const idx = prev.indexOf(item)
				if (idx === -1) return [...prev, item]
				const clone = [...prev]
				clone.splice(idx, 1)
				return clone
			}),
		[setValue],
	)

	const defineValue = useCallback(
		(item: string, active: boolean) =>
			setValue(prev => {
				const idx = prev.indexOf(item)
				if (active) {
					if (idx === -1) return [...prev, item]
					else return prev
				} else {
					if (idx === -1) return prev
					const clone = [...prev]
					clone.splice(idx, 1)
					return clone
				}
			}),
		[setValue],
	)

	return [value, setValue, toggleValue, defineValue]
}
